// i18nCommon.js
'use strict';
import env from './env.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

export const DEFAULT_LANGUAGE = 'zh-TW';
export const PREFERRED_LOCALES = ['en', 'zh-TW', 'cn', 'ja', 'th', 'vi', 'ko'];
export const LANGUAGE_QUERY_NAME = 'lang';

const LANGUAGE_REGEX = /^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{0,5}){0,2}-*$/;

export const localeConvertor = ({
  locale,
  isISO639 = false,
  isBCP47 = false,
}) => {
  let lng = typeof locale === 'string' ? locale : locale?.[0];
  switch (lng?.toLowerCase?.()) {
    case 'zh':
    case 'zh-cn':
    case 'zh-hans':
    case 'zh-hans-cn':
    case 'cn':
      if (isISO639) {
        return 'zh';
      } else if (isBCP47) {
        return 'zh-Hans';
      } else {
        return 'zh-CN';
      }
    case 'ja-jp':
      return 'ja';
    case 'en':
    case 'en-us':
      return 'en';
    default:
      if (lng?.startsWith?.('zh-')) {
        if (isISO639) {
          return 'zh-hant';
        } else if (isBCP47) {
          return 'zh-Hant';
        } else {
          return 'zh-TW';
        }
      }
      if (lng && LANGUAGE_REGEX.exec(lng)) {
        return lng.replace(/-*$/, '');
      }
      return 'en';
  }
};

export const i18nCommonOption = {
  fallbackLng: code => {
    const language = code?.toLowerCase?.();
    if (language?.startsWith?.('zh-')) {
      return ['zh-TW', 'en'];
    } else {
      return ['en'];
    }
  },
  interpolation: { prefix: '{', suffix: '}', skipOnVariables: false },
  debug: env.DEBUG_I18N === 'true',
  fallbackNS: TranslationNamespace.DEFAULT,
};

export const i18CommonHttpBackendOption = {
  loadPath: (locale, namespace) => `${locale}|${namespace}`, // request endpoint
  parse: data => data, // to ignore `i18next-http-backend` JSON.parse
};

/**
 * language code validator
 * @param {object} options
 * @param {string} options.code
 * @returns {boolean}
 */
export const languageCodeValidator = ({ code }) => {
  if (!code || typeof code !== 'string') return false;
  return LANGUAGE_REGEX.test(code);
};

export const supportedLanguageCodeValidator = ({ lang }) => {
  if (!languageCodeValidator({ code: lang })) return false;

  // If the language code starts with 'zh', only allow 'zh-TW' or 'zh-CN' (after normalization by localeConvertor) as supported languages
  if (lang.startsWith('zh')) {
    return ['zh-TW', 'zh-CN'].includes(localeConvertor({ locale: lang }));
  }

  return PREFERRED_LOCALES.includes(lang);
};

export const DEFAULT_USED_I18N_KEYS = [
  'console_warning',
  'iwin_landing_page_sec_cta',
  'iwin_landing_page_title',
  'iwin_landing_page_subtitle',
  'iwin_landing_page_cta',
  'iwin_landing_page_statistic_1',
  'iwin_landing_page_statistic_2',
  'iwin_landing_page_statistic_3',
  'iwin_landing_page_cta',
  'iwin_landing_page_section_1_title',
  'iwin_landing_page_section_1_body_1',
  'iwin_landing_page_section_1_body_2',
  'iwin_landing_page_section_1_body_3',
  'iwin_landing_page_section_2_title',
  'iwin_landing_page_section_2_body_1',
  'iwin_landing_page_section_2_body_2',
  'iwin_landing_page_section_2_body_3',
  'iwin_landing_page_section_3_title',
  'iwin_landing_page_section_3_body_1',
  'iwin_landing_page_section_3_body_2',
  'iwin_landing_page_section_3_body_3',
  'overlay_header_happyhour',
  'overlay_instruction_happyhour',
  'warning',
  'age_verify_title',
  'age_verify_description',
  'terms_of_service',
  'agree_terms_of_service_button_highlight',
  'exit',
];
